import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useBoqs } from "../../api/boq";
import {
  useAttachOrderDocument,
  useLinkOrder,
  useReceiveOrder,
  useReviseOrder,
  type CustomerOrder,
} from "../../api/customerOrders";
import { formatDate, formatDateTime } from "../../api/dates";
import { formatMoney } from "../../api/money";
import { StatusChip } from "../../components/ui";
import { jobLink } from "../commercial/pipeline";
import { customerOrderLabel, customerOrderTone, humanise } from "../commercial/tones";

/** The City's purchase order on its job: capture one when it arrives, and
 * reconcile it against the bill it authorises and the invoice raised on it.
 * The register that used to be its own screen is the commercial rail now; the
 * order is worked on its job's Customer PO step. */

interface ReceiveProps {
  /** The bill this order answers, when it is captured on its job; without
   * one the office picks the bill. */
  boqId?: number;
  onReceived?: (order: CustomerOrder) => void;
  onCancel?: () => void;
}

export function ReceiveForm({ boqId, onReceived, onCancel }: ReceiveProps) {
  const receive = useReceiveOrder();
  const boqs = useBoqs("", boqId === undefined);
  const [number, setNumber] = useState("");
  const [value, setValue] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [chosen, setChosen] = useState("");
  const [notes, setNotes] = useState("");
  const against = boqId ?? (chosen === "" ? null : Number(chosen));

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    receive.mutate(
      {
        number: number.trim(),
        value,
        order_date: orderDate || null,
        boq_id: against,
        notes: notes.trim() || null,
      },
      {
        onSuccess: (order) => {
          setNumber("");
          setValue("");
          onReceived?.(order);
        },
      },
    );
  }

  return (
    <form className="header-form cm-form" onSubmit={onSubmit} aria-label="Capture the City's order">
      <label>
        Order number
        <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="4500123456" required />
      </label>
      <label>
        Value excl VAT
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="31 837,50" className="num" required />
      </label>
      <label>
        Order date
        <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
      </label>
      {boqId === undefined ? (
        <label>
          Authorises BOQ
          <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
            <option value="">Not yet linked</option>
            {(boqs.data?.rows ?? []).map((boq) => (
              <option key={boq.id} value={boq.id}>
                {boq.name} — {formatMoney(boq.total_excl_vat)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="wide">
        Notes
        <input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="cm-form-actions">
        <button type="submit" className="btn btn-primary" disabled={receive.isPending || number.trim() === "" || value.trim() === ""}>
          Record order
        </button>
        {onCancel ? (
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
      {receive.isError ? <p className="notice bad">{receive.error.message}</p> : null}
    </form>
  );
}

export function OrderView({ order }: { order: CustomerOrder }) {
  const revise = useReviseOrder(order.id);
  const link = useLinkOrder(order.id);
  const attach = useAttachOrderDocument(order.id);
  const boqs = useBoqs("", order.boq === null);
  const [revised, setRevised] = useState("");
  const [reason, setReason] = useState("");
  const [boqId, setBoqId] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const gap = order.gaps.poGap && order.gaps.poGap.startsWith("-") ? order.gaps.poGap.slice(1) : order.gaps.poGap;

  return (
    <section className="cm-docview" aria-label={`Customer order ${order.orderNumber}`}>
      <div className="cm-doc-head">
        <h2 className="mono">{order.orderNumber}</h2>
        <StatusChip tone={order.overAuthority ? "crit" : order.reconciled ? "ok" : customerOrderTone(order.status)}>
          {order.overAuthority ? "Over authority" : order.reconciled ? "Reconciled" : customerOrderLabel(order.status)}
        </StatusChip>
        {order.orderDate ? <span className="cm-doc-meta">Dated {formatDate(order.orderDate)}</span> : null}
      </div>

      <div className="cm-recon">
        <div>
          <span>The City's order</span>
          <strong>{formatMoney(order.values.poTotal)}</strong>
        </div>
        <div>
          <span>Our bill</span>
          <strong>{formatMoney(order.values.boqTotal)}</strong>
        </div>
        <div>
          <span>Invoiced</span>
          <strong>{formatMoney(order.values.invoiceTotal)}</strong>
        </div>
      </div>
      <p className="cm-doc-note">
        {order.headline}
        {order.values.revisedPo ? ` Revised from ${formatMoney(order.values.originalPo)} to ${formatMoney(order.values.revisedPo)}.` : ""}
        {gap ? ` ${order.overAuthority ? "The bill exceeds the order by" : "Still unbilled:"} ${formatMoney(gap)}.` : ""}
      </p>

      <dl className="cm-facts">
        <div>
          <dt>Authorises</dt>
          <dd>
            {order.boq ? (
              <Link to={order.boq.quotation_id ? jobLink(order.boq.quotation_id, "boq") : `/boq/${order.boq.id}`}>
                {order.boq.name}
              </Link>
            ) : (
              <form
                className="inline-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  link.mutate(Number(boqId));
                }}
              >
                <select value={boqId} onChange={(e) => setBoqId(e.target.value)} aria-label="BOQ to link">
                  <option value="">Choose the BOQ…</option>
                  {(boqs.data?.rows ?? []).map((boq) => (
                    <option key={boq.id} value={boq.id}>
                      {boq.name}
                    </option>
                  ))}
                </select>
                <button type="submit" className="row-button" disabled={link.isPending || boqId === ""}>
                  Link
                </button>
              </form>
            )}
            {link.isError ? <span className="line-warn"> {link.error.message}</span> : null}
          </dd>
        </div>
        <div>
          <dt>The City's document</dt>
          <dd>
            {order.documentUrl ? (
              <a href={order.documentUrl} target="_blank" rel="noreferrer">
                {order.document}
              </a>
            ) : (
              <span className="muted">not attached</span>
            )}{" "}
            <input
              ref={input}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  attach.mutate(file);
                }
                event.target.value = "";
              }}
            />
            <button type="button" className="row-button" onClick={() => input.current?.click()} disabled={attach.isPending}>
              {order.documentUrl ? "Replace" : "Attach"}
            </button>
            {attach.isError ? <span className="line-warn"> {attach.error.message}</span> : null}
          </dd>
        </div>
        {order.notes ? (
          <div>
            <dt>Notes</dt>
            <dd>{order.notes}</dd>
          </div>
        ) : null}
      </dl>

      <section className="cm-block">
        <h3>Record an amendment</h3>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            revise.mutate({ revised, reason: reason.trim() || null }, { onSuccess: () => setRevised("") });
          }}
        >
          <label>
            Revised value excl VAT
            <input value={revised} onChange={(e) => setRevised(e.target.value)} className="num" required />
          </label>
          <label>
            Reason
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-secondary" disabled={revise.isPending || revised.trim() === ""}>
            Revise
          </button>
        </form>
        <p className="cm-doc-note">The original value is never touched; every amendment is recorded below.</p>
        {revise.isError ? <p className="notice bad">{revise.error.message}</p> : null}
      </section>

      <section className="cm-block">
        <h3>Everything that happened</h3>
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>By</th>
                <th className="num">From</th>
                <th className="num">To</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {order.history.map((event, index) => (
                <tr key={`${event.at}-${index}`}>
                  <td>{formatDateTime(event.at)}</td>
                  <td>{humanise(event.action)}</td>
                  <td>{event.by ?? "—"}</td>
                  <td className="num">{formatMoney(event.from)}</td>
                  <td className="num">{formatMoney(event.to)}</td>
                  <td>{event.detail ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
